/* ================================================================
   Mor & Meadow — shared client behaviour for theme sections.
   Drop into /assets and include from theme.liquid (defer):
     <script src="{{ 'morandmeadow.js' | asset_url }}" defer></script>
   Public API on `window.MAM`:
     • MAM.observeReveals(root?)         — wires up [data-mam-reveal]
     • MAM.observeWordReveals(root?)     — wires up [data-mam-word-reveal]
     • MAM.splitWordReveal(el)           — splits an element's text into spans
     • MAM.onReady(fn)                    — DOMContentLoaded shim
   Section files include their own `{% javascript %}` block where they
   call these on render so each section is self-contained.
   ================================================================ */
(function () {
  "use strict";

  const MAM = (window.MAM = window.MAM || {});

  /* — DOM ready ————————————————————————————————————————— */
  MAM.onReady = function (fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  };

  /* — Generic reveal observer ——————————————————————————————
     Adds .is-shown to any [data-mam-reveal] when 15% of the element
     intersects the viewport. Plays once. */
  let revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    if (!("IntersectionObserver" in window)) return null;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-shown");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.01 }
    );
    return revealObserver;
  }

  MAM.observeReveals = function (root) {
    const obs = getRevealObserver();
    const scope = root || document;
    const els = scope.querySelectorAll("[data-mam-reveal]:not(.is-shown)");
    if (!obs) {
      // Fallback: just show everything.
      els.forEach(function (el) { el.classList.add("is-shown"); });
      return;
    }
    els.forEach(function (el) { obs.observe(el); });
  };

  /* — Word-reveal splitter —————————————————————————————————
     Takes an element marked [data-mam-word-reveal] containing plain
     text, splits it into <span class="mam-word">…</span> tokens,
     preserving inline <em>/<i>/<u>/<a> children as-is wrapped in their
     own mam-word span. */
  MAM.splitWordReveal = function (el) {
    if (el.dataset.mamSplit === "1") return;
    el.dataset.mamSplit = "1";
    const original = Array.from(el.childNodes);
    const frag = document.createDocumentFragment();
    original.forEach(function (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const words = (node.textContent || "").split(/\s+/).filter(Boolean);
        words.forEach(function (w) {
          const span = document.createElement("span");
          span.className = "mam-word";
          span.textContent = w;
          frag.appendChild(span);
          frag.appendChild(document.createTextNode(" "));
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Inline element — try to split its text into individual word tokens
        // while preserving the element wrapper (useful for <em>, <u>, <i>, <a>).
        const onlyTextChildren = Array.from(node.childNodes).every(function (n) {
          return n.nodeType === Node.TEXT_NODE;
        });
        if (onlyTextChildren) {
          const words = (node.textContent || "").split(/\s+/).filter(Boolean);
          words.forEach(function (w) {
            const span = document.createElement("span");
            span.className = "mam-word";
            const clone = node.cloneNode(true);
            // Replace clone content with the single word so styling stays.
            clone.textContent = w;
            span.appendChild(clone);
            frag.appendChild(span);
            frag.appendChild(document.createTextNode(" "));
          });
        } else {
          // Fallback: wrap the whole element as a single token.
          const span = document.createElement("span");
          span.className = "mam-word";
          span.appendChild(node.cloneNode(true));
          frag.appendChild(span);
          frag.appendChild(document.createTextNode(" "));
        }
      }
    });
    el.replaceChildren(frag);
  };

  let wordRevealObserver = null;
  function getWordRevealObserver() {
    if (wordRevealObserver) return wordRevealObserver;
    if (!("IntersectionObserver" in window)) return null;
    wordRevealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Stagger: each word picks up its own transition-delay.
            const words = entry.target.querySelectorAll(".mam-word");
            words.forEach(function (w, i) {
              w.style.transitionDelay = (i * 60 + 80) + "ms";
            });
            entry.target.classList.add("is-shown");
            wordRevealObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    return wordRevealObserver;
  }

  MAM.observeWordReveals = function (root) {
    const scope = root || document;
    const els = scope.querySelectorAll("[data-mam-word-reveal]:not(.is-shown)");
    els.forEach(function (el) { MAM.splitWordReveal(el); });
    const obs = getWordRevealObserver();
    if (!obs) {
      els.forEach(function (el) { el.classList.add("is-shown"); });
      return;
    }
    els.forEach(function (el) { obs.observe(el); });
  };

  /* — Auto-init on first load + on Shopify theme-editor section reload — */
  MAM.onReady(function () {
    MAM.observeReveals();
    MAM.observeWordReveals();
  });

  document.addEventListener("shopify:section:load", function (e) {
    MAM.observeReveals(e.target);
    MAM.observeWordReveals(e.target);
  });

  /* — Modal helper (used by Instagram grid) ————————————————— */
  MAM.openModal = function (modalEl) {
    if (!modalEl) return;
    modalEl.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
    requestAnimationFrame(function () {
      modalEl.classList.add("is-open");
    });
  };
  MAM.closeModal = function (modalEl) {
    if (!modalEl) return;
    modalEl.classList.remove("is-open");
    document.body.style.overflow = "";
    setTimeout(function () { modalEl.setAttribute("hidden", ""); }, 350);
  };
})();
